import { COLORS, FONTS } from "@/lib/constants";

const CORAL = "var(--hl-coral)";
const PURPLE = "var(--hl-purple)";
const GREEN = "var(--hl-green)";

// The three YUO tokens have both palette-named and semantic aliases:
//   coral / feeling  → warmth, emotion
//   purple / craft   → work, making, art
//   green  / growth  → learning, transformation
const COLOR = {
  coral: CORAL, feeling: CORAL,
  purple: PURPLE, craft: PURPLE,
  green: GREEN, growth: GREEN,
};

// Match ANY [[kind:word]] tag, not just the three known kinds — an unknown
// kind must still render as clean text rather than leaking the raw markup.
const RX = /\[\[([a-zA-Z]+):([^\]]+)\]\]/g;

// Inline markdown emphasis the model routinely emits: **bold**, __bold__ and
// *italic*. Without this the asterisks render literally. Delimiters must hug
// non-space characters so arithmetic ("2 * 3 * 4") is left alone, and single
// underscores are deliberately NOT treated as italic so snake_case survives.
const MD_RX = /(\*\*[^*]+\*\*|__[^_]+__|\*[^\s*][^*\n]*[^\s*]\*|\*[^\s*]\*)/g;

export function parseHighlights(text) {
  const hits = [];
  const plain = text.replace(RX, (_m, k, w) => {
    hits.push({ kind: k, word: w });
    return w;
  });
  return { plain, hits };
}

/** Strip color tags and markdown emphasis down to plain readable text. */
export function toPlainText(text) {
  return (text || "")
    .replace(RX, (_m, _k, w) => w)
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^\s*][^*\n]*[^\s*]|[^\s*])\*/g, "$1");
}
function renderEmphasis(text, keyPrefix) {
  return text
    .split(MD_RX)
    .filter((p) => p !== "" && p !== undefined)
    .map((p, i) => {
      const k = `${keyPrefix}-${i}`;
      if (p.length > 4 && ((p.startsWith("**") && p.endsWith("**")) || (p.startsWith("__") && p.endsWith("__")))) {
        return (
          <strong key={k} style={{ fontWeight: 700, color: "var(--yuo-chat-bold)" }}>
            {p.slice(2, -2)}
          </strong>
        );
      }
      if (p.length > 2 && p.startsWith("*") && p.endsWith("*")) {
        return <em key={k} style={{ color: "var(--yuo-chat-italic)" }}>{p.slice(1, -1)}</em>;
      }
      return <span key={k}>{p}</span>;
    });
}

/**
 * Render assistant text where words wrapped as [[color:word]] become colored,
 * tappable spans. Click invokes onWordPress(word, kind). Everything else is
 * rendered with inline markdown emphasis applied so no raw markup is shown.
 */
export function HighlightedText({ content, onWordPress, baseStyle }) {
  const src = content || "";
  const parts = [];
  let lastIndex = 0;
  let m;
  const rx = new RegExp(RX.source, "g");
  while ((m = rx.exec(src)) !== null) {
    if (m.index > lastIndex) parts.push({ text: src.slice(lastIndex, m.index) });
    parts.push({ text: m[2], kind: m[1].toLowerCase() });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < src.length) parts.push({ text: src.slice(lastIndex) });

  return (
    <span style={{ whiteSpace: "pre-wrap", ...baseStyle }}>
      {parts.map((p, i) => {
        const known = p.kind && COLOR[p.kind];
        if (!p.kind) return <span key={i}>{renderEmphasis(p.text, `t${i}`)}</span>;
        if (!known) {
          // Unknown color kind — show the word plainly instead of the raw tag.
          return <span key={i}>{renderEmphasis(p.text, `u${i}`)}</span>;
        }
        return (
          <span
            key={i}
            onClick={onWordPress ? () => onWordPress(p.text, p.kind) : undefined}
            style={{
              color: COLOR[p.kind],
              fontWeight: 600,
              textDecoration: onWordPress ? "underline dotted" : "none",
              cursor: onWordPress ? "pointer" : "inherit",
            }}
            data-testid={`highlight-${p.kind}-${p.text.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {p.text}
          </span>
        );
      })}
    </span>
  );
}

export function HighlightLegend() {
  return (
    <div
      data-testid="highlight-legend"
      style={{
        display: "flex",
        gap: 12,
        padding: "4px 16px 6px",
      }}
    >
      <LegendDot color={CORAL} label="feeling" />
      <LegendDot color={PURPLE} label="craft" />
      <LegendDot color={GREEN} label="growth" />
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
      <span style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 10 }}>
        {label}
      </span>
    </div>
  );
}
